// src/pages/Coleta/components/EvolucaoChart.jsx
import React, { useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Box, Button, Paper, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';
import * as Lucide from 'lucide-react';
import dayjs from 'dayjs';
import {
    formatarContagem,
    formatarContagemCompacta,
    formatarLog10,
    marcasDiarias,
    ordenhasDaSerie,
    valorContagem,
} from '../../../utils/coleta';
import { AZUL_CLARO } from '../../../theme/cores';
import IndicadoresDialog from './IndicadoresDialog';
import ComparacaoPontos from './ComparacaoPontos';

const SEM_PONTO = '__sem_ponto__';
const ALTURA = 300;

/** Cores das linhas, na ordem dos pontos de coleta (escuro → claro, como no processo). */
const CORES = ['#1E3A5F', '#2F80ED', '#5BA4A4', '#9B6BCB', '#E0A43A', '#D9644A'];
/** Cores das linhas de referência (legislação), na ordem em que foram cadastradas. */
const CORES_REFERENCIA = ['#C8692B', '#7E57C2', '#2E7D32', '#00838F', '#AD1457'];

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

function Dica({ active, payload, unidade }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {dayjs(p.t).format('DD/MM/YYYY HH:mm')} · {p.ponto}
            </Typography>
            <Typography fontWeight={700}>
                {formatarContagem(p.log10)} {unidade}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">{formatarLog10(p.log10)}</Typography>
            <Typography variant="caption" color="text.secondary">{p.identificacao}</Typography>
        </Paper>
    );
}

/** Caixa de seleção preenchida na cor da série, com o ✓ branco (vazia quando desligada). */
function CaixaSerie({ cor, marcada }) {
    return (
        <Box
            sx={{
                width: 18, height: 18, borderRadius: '4px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: marcada ? cor : 'transparent',
                border: '2px solid', borderColor: cor,
            }}
        >
            {marcada && <Lucide.Check size={13} strokeWidth={3.5} color="#fff" />}
        </Box>
    );
}

/**
 * Evolução da contagem por data de coleta, uma linha por ponto de coleta, com as
 * linhas de referência da página (legislação e metas da propriedade, vindas do servidor
 * e escolhidas em "Legislação") e a comparação de uma ordenha entre os pontos.
 */
export default function EvolucaoChart({
    serie,
    pontos,
    unidade,
    rotuloMedida,
    carregando,
    periodo,
    referencias,
    podeEditarReferencias,
    onSalvarReferencias,
    onAbrirAmostra,
    pontosDaAnalise,
}) {
    const theme = useTheme();
    const [ocultos, setOcultos] = useState(() => new Set());
    const [editandoIndicadores, setEditandoIndicadores] = useState(false);
    const [comparando, setComparando] = useState(false);
    const [ordenhaSelecionada, setOrdenhaSelecionada] = useState(null);

    const pontoPorId = useMemo(() => new Map(pontos.map((p) => [p.id, p])), [pontos]);
    const nomePonto = (id) => (id ? pontoPorId.get(id)?.nome || 'Ponto removido' : null);
    const ordemPonto = (id) => (id ? pontoPorId.get(id)?.ordem ?? 9999 : Number.MAX_SAFE_INTEGER);

    const linhas = useMemo(() => {
        const grupos = new Map();
        (serie || []).forEach((s) => {
            const chave = s.pontoColetaId || SEM_PONTO;
            if (!grupos.has(chave)) grupos.set(chave, []);
            grupos.get(chave).push({
                t: new Date(s.dataHora).getTime(),
                valor: valorContagem(s.log10Contagem),
                log10: s.log10Contagem,
                identificacao: s.identificacao,
                ponto: pontoPorId.get(s.pontoColetaId)?.nome || 'Sem ponto',
            });
        });
        return [...grupos.entries()]
            .map(([chave, dados]) => ({
                chave,
                nome: chave === SEM_PONTO ? 'Sem ponto' : pontoPorId.get(chave)?.nome || 'Ponto removido',
                ordem: chave === SEM_PONTO ? Number.MAX_SAFE_INTEGER : pontoPorId.get(chave)?.ordem ?? 9999,
                dados: dados.sort((a, b) => a.t - b.t),
            }))
            .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome))
            .map((l, i) => ({ ...l, cor: CORES[i % CORES.length] }));
    }, [serie, pontoPorId]);

    const ordenhas = useMemo(() => ordenhasDaSerie(serie), [serie]);

    // Cor de cada ponto, a MESMA da sua linha, para os cards da comparacao. A primeira
    // cor (azul-escuro, quase preto) nao pinta o card: fica no estilo neutro.
    const corPorPonto = useMemo(
        () => new Map(linhas.filter((l) => l.cor !== CORES[0]).map((l) => [l.chave === SEM_PONTO ? null : l.chave, l.cor])),
        [linhas]
    );
    const ordenhaAtual = ordenhas.find((o) => o.chave === ordenhaSelecionada) || ordenhas[0];

    // Cor fixa pela posição na lista inteira: ocultar uma linha não troca a cor das outras.
    const referenciasVisiveis = (referencias?.itens || [])
        .map((ref, i) => ({ ...ref, cor: CORES_REFERENCIA[i % CORES_REFERENCIA.length] }))
        .filter((ref) => ref.exibir && Number.isFinite(ref.valor));

    // Eixo X: o período inteiro do filtro, dia a dia (como no protótipo), não só onde há dado.
    const inicio = periodo?.de ? new Date(periodo.de).getTime() : 'dataMin';
    const fim = periodo?.ate ? new Date(periodo.ate).getTime() : 'dataMax';
    const marcas = periodo?.de && periodo?.ate ? marcasDiarias(periodo.de, periodo.ate) : undefined;

    const alternar = (chave) =>
        setOcultos((atual) => {
            const proximo = new Set(atual);
            if (proximo.has(chave)) proximo.delete(chave);
            else proximo.add(chave);
            return proximo;
        });

    return (
        <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ sm: 'flex-start' }}
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>Evolução da {rotuloMedida}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Contagem por data de coleta · {unidade}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<Lucide.SlidersHorizontal size={16} />}
                            onClick={() => setEditandoIndicadores(true)}
                            disabled={!referencias || referencias.erro}
                            sx={{ borderRadius: 2, borderColor: 'divider', fontWeight: 600 }}
                        >
                            Legislação
                        </Button>
                        <Button
                            variant={comparando ? 'outlined' : 'text'}
                            startIcon={<Lucide.SlidersHorizontal size={16} />}
                            onClick={() => setComparando((c) => !c)}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                color: AZUL_CLARO,
                                borderColor: alpha(AZUL_CLARO, 0.5),
                                "&:hover": { bgcolor: alpha(AZUL_CLARO, 0.08), borderColor: AZUL_CLARO },
                            }}
                        >
                            {comparando ? 'Fechar comparação' : 'Comparar pontos'}
                        </Button>
                    </Stack>
                </Stack>

                {linhas.length > 1 && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                        {linhas.map((l) => {
                            const visivel = !ocultos.has(l.chave);
                            return (
                                <Box
                                    key={l.chave}
                                    component="button"
                                    type="button"
                                    role="checkbox"
                                    aria-checked={visivel}
                                    onClick={() => alternar(l.chave)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                        px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                                        font: 'inherit', fontSize: '0.9rem', textAlign: 'left',
                                        color: visivel ? 'text.primary' : 'text.secondary',
                                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                                        border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.15),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                                    }}
                                >
                                    <CaixaSerie cor={l.cor} marcada={visivel} />
                                    {l.nome}
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                {carregando ? (
                    <Skeleton variant="rounded" height={ALTURA} />
                ) : linhas.length === 0 ? (
                    <Box sx={{ height: ALTURA, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, color: 'text.secondary' }}>
                        <Lucide.ChartLine size={28} />
                        <Typography variant="body2">Nenhuma contagem processada no período.</Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={ALTURA}>
                        <LineChart margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis
                                dataKey="t"
                                type="number"
                                scale="time"
                                domain={[inicio, fim]}
                                ticks={marcas}
                                tickFormatter={(t) => dayjs(t).format('DD/MM')}
                                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                tickLine={false}
                                axisLine={false}
                                allowDuplicatedCategory={false}
                            />
                            <YAxis
                                dataKey="valor"
                                domain={['auto', 'auto']}
                                tickFormatter={formatarContagemCompacta}
                                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                tickLine={false}
                                axisLine={false}
                                width={56}
                            />
                            <Tooltip content={(props) => <Dica {...props} unidade={unidade} />} />
                            {referenciasVisiveis.map((ref) => (
                                <ReferenceLine
                                    key={ref.id}
                                    y={ref.valor}
                                    stroke={ref.cor}
                                    strokeDasharray="6 4"
                                    strokeWidth={1.5}
                                    ifOverflow="hidden"
                                    label={{ value: INTEIRO.format(ref.valor), position: 'insideBottomRight', fill: ref.cor, fontSize: 11 }}
                                />
                            ))}
                            {comparando && ordenhaAtual && (
                                <ReferenceLine x={ordenhaAtual.data.getTime()} stroke={theme.palette.text.disabled} ifOverflow="hidden" />
                            )}
                            {linhas
                                .filter((l) => !ocultos.has(l.chave))
                                .map((l) => (
                                    <Line
                                        key={l.chave}
                                        data={l.dados}
                                        dataKey="valor"
                                        name={l.nome}
                                        type="monotone"
                                        stroke={l.cor}
                                        strokeWidth={2}
                                        dot={{ r: 3, strokeWidth: 1.5, fill: theme.palette.background.paper }}
                                        activeDot={{ r: 5 }}
                                        isAnimationActive={false}
                                    />
                                ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}

                {referenciasVisiveis.length > 0 && (
                    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                        {referenciasVisiveis.map((ref) => (
                            <Box
                                key={ref.id}
                                sx={{ px: 1.5, py: 1, borderRadius: 2, border: '1px dashed', borderColor: ref.cor }}
                            >
                                <Typography fontWeight={700} sx={{ color: ref.cor, fontSize: '0.95rem' }}>
                                    {INTEIRO.format(ref.valor)} {ref.unidade}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {ref.tipo === 'LEGISLACAO' ? ref.fonte : `${ref.nome} · meta da propriedade`}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Paper>

            {comparando && (
                <ComparacaoPontos
                    ordenhas={ordenhas}
                    selecionada={ordenhaAtual?.chave}
                    onSelecionar={setOrdenhaSelecionada}
                    nomePonto={nomePonto}
                    corDoPonto={(id) => corPorPonto.get(id || null) || null}
                    ordemPonto={ordemPonto}
                    pontosDaAnalise={pontosDaAnalise}
                    unidade={unidade}
                    rotuloMedida={rotuloMedida}
                    onAbrirAmostra={onAbrirAmostra}
                />
            )}

            <IndicadoresDialog
                open={editandoIndicadores}
                onClose={() => setEditandoIndicadores(false)}
                referencias={referencias}
                podeEditar={Boolean(podeEditarReferencias && onSalvarReferencias)}
                onSalvar={async (payload) => {
                    await onSalvarReferencias(payload);
                    setEditandoIndicadores(false);
                }}
            />
        </Stack>
    );
}
